/* eslint-disable no-restricted-syntax -- The video transport is chrome painted
   ON TOP of arbitrary media, so it is white regardless of Mora's theme. Same
   exemption `InstagramReel.tsx`, `TikTokVideo.tsx` and `SafeZoneOverlay.tsx`
   already carry, for the same reason: a semantic token here would invert the
   controls against the footage and make them unreadable on a light video. */
import React from 'react';

import { MediaSlot } from './MediaSlot';
import {
  PlatformExtendIcon,
  PlatformMoreHorizIcon,
  PlatformPlayIcon,
  PlatformTwCommentIcon,
  PlatformTwLikeIcon,
  PlatformTwRetweetIcon,
  PlatformTwShareIcon,
  PlatformVolumeMuteIcon,
} from './PlatformIcons';
import { PreviewAvatar } from './PreviewAvatar';
import { PLATFORM_SKINS } from './platform-tokens';
import { compactNumber } from './types';
import { useResolvedDarkMode } from './useResolvedDarkMode';

import { cn } from '@/lib/utils';

/**
 * X (Twitter) PROMOTED POST — the paid sibling of `XPost`.
 *
 * ── PROVENANCE ────────────────────────────────────────────────────────────
 * Ported from Figma `Dl0gE7lH27N7x7OJb0DFBw`, frames `28:13542` (light) and
 * `28:13541` (dark), read 2026-08-21. The frame is 539px wide. Its geometry
 * is unusual and deliberately preserved: the header and body text sit in a
 * 16/66px column (avatar at 16, name and body at 66, the `⋯` at 513), while
 * the MEDIA block and the engagement row below it sit in their own 32px
 * gutter (475px wide, x=32 → 507). Real X aligns media with the text column;
 * this file does not, because the frame is the source of truth for
 * structure and a "correction" here would be an invention.
 *
 * Colours are `PLATFORM_SKINS.twitter`, ported from those same frames into
 * `--platform-twitter-*`. Note the skin has no `linkText`: X's ad card has
 * no distinct accent hue — the black Follow pill is the only brand signal,
 * and it is `ctaBackground`/`ctaText`, black-on-white in BOTH themes.
 *
 * ── WHAT THE FIGMA SHOWS THAT THIS DELIBERATELY DOES NOT RENDER ───────────
 * The frame prints a brand called "Club Doggo", the counts 12 / 5 / 66, a
 * playhead sitting ~4% into the video, and the timecode "0:00 / 0:34". Three
 * of those four are DATA, and this program's rule is structure and skin from
 * Figma, never the data (`__tests__/engagementTruth.test.tsx` enforces it
 * across the family). So:
 *
 * - Engagement renders only when the caller passes it, and then renders
 *   exactly what it was given INCLUDING zero. An unpublished draft shows no
 *   counts at all — not zeroes, not the Figma's numbers.
 * - The transport row renders only when a real `videoDurationSeconds` is
 *   known, and the playhead is pinned at 0. An unplayed video IS at 0:00;
 *   a 4%-elapsed playhead on a static preview is a small fabrication of the
 *   same kind, and `InstagramReel`/`InstagramStory` already carry hardcoded
 *   fractional progress bars that this file declines to copy.
 * - `Follow` is chrome, not data. Every promoted X post carries it, so it
 *   is always present.
 *
 * ── NOT A `PlatformPreviewKind` ───────────────────────────────────────────
 * Same reason as `MetaAdPreview`/`InstagramAdPreview`/`LinkedInAdPreview`:
 * the dispatcher maps a stored `posts.platforms` value to a shell, and an ad
 * unit never carries one.
 */
export interface TwitterAdPreviewProps {
  brandName: string;
  brandColor?: string;
  avatarUrl?: string | null;
  /** The promoted post's body text. */
  bodyText: string;
  imageUrl?: string | null;
  videoUrl?: string | null;
  isLoading?: boolean;
  emptyHint?: string;
  /**
   * Runtime of the attached video, in seconds. Supplying it is what makes
   * the transport row appear — omit it and no timecode is claimed. The
   * playhead always sits at 0; see the file header.
   */
  videoDurationSeconds?: number | null;
  /**
   * Real engagement on a PUBLISHED promoted post only. Omit entirely for a
   * draft — the count row does not render at all without it, matching every
   * other replica in this directory. When present, renders exactly what it
   * is given, including zero.
   */
  engagement?: {
    replies?: number;
    reposts?: number;
    likes?: number;
  };
  /**
   * Forces which X skin variant renders, independent of Mora's active theme.
   * Omit to follow Mora's theme (the normal, real-app path); pass explicitly
   * for the style guide's side-by-side comparison and for tests that must
   * render a deterministic variant.
   */
  theme?: 'light' | 'dark';
  /**
   * Rendered INSIDE a frame that already sizes it. Standalone the card pins
   * itself to the Figma frame's width and centres. Same contract as
   * `MetaAdPreview.embedded`, and like it, this does NOT strip the card's
   * own border, radius or fill — a post preview has to read as a POST
   * sitting ON something.
   */
  embedded?: boolean;
  className?: string;
}

/** The Figma frame's own width (`28:13542` is 539px). */
const TWITTER_AD_MAX_WIDTH = 539;

/** Image 475×285 on the media frame — the ratio the frame actually draws. */
const TWITTER_AD_MEDIA_RATIO = 475 / 285;

function timecode(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export const TwitterAdPreview: React.FC<TwitterAdPreviewProps> = ({
  brandName,
  brandColor,
  avatarUrl,
  bodyText,
  imageUrl,
  videoUrl,
  isLoading = false,
  emptyHint = 'No creative yet — copy only',
  videoDurationSeconds,
  engagement,
  theme,
  embedded = false,
  className,
}) => {
  const isDarkMode = useResolvedDarkMode();
  const resolvedTheme = theme ?? (isDarkMode ? 'dark' : 'light');
  const skin = PLATFORM_SKINS.twitter[resolvedTheme];

  const hasMedia = Boolean(imageUrl) || Boolean(videoUrl) || isLoading;
  const hasDuration = typeof videoDurationSeconds === 'number' && videoDurationSeconds > 0;
  const replies = engagement?.replies;
  const reposts = engagement?.reposts;
  const likes = engagement?.likes;
  const hasEngagement = replies != null || reposts != null || likes != null;

  return (
    <div
      className={cn(embedded ? 'w-full' : 'mx-auto w-full', className)}
      style={embedded ? undefined : { maxWidth: `${TWITTER_AD_MAX_WIDTH}px` }}
    >
      <article
        className="w-full overflow-hidden rounded-lg shadow-elevation-1"
        data-platform="twitter-ad"
        style={{ backgroundColor: skin.cardBackground }}
      >
        <header className="flex items-start justify-between gap-3 px-4 pb-1 pt-3">
          <div className="flex min-w-0 items-center gap-3">
            <PreviewAvatar
              brandName={brandName}
              brandColor={brandColor}
              avatarUrl={avatarUrl}
              className="size-9 rounded-full"
              textClassName="text-caption font-semibold"
            />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold" style={{ color: skin.primaryText }}>
                {brandName}
              </p>
              {/*
                "Sponsored" and nothing else. The frame shows no handle, and
                inventing one from the brand name is the same class of
                fabrication as inventing a follower count — `@clubdoggo` would
                be a plausible-looking value nobody entered.
              */}
              <p className="text-detail" style={{ color: skin.secondaryText }}>
                Sponsored
              </p>
            </div>
          </div>
          <PlatformMoreHorizIcon
            size={14}
            className="mt-2 shrink-0"
            style={{ color: skin.secondaryText }}
          />
        </header>

        {/* x=66 in the frame: 16px card padding + 36px avatar + 14px gap. */}
        <div className="whitespace-pre-line px-4 pb-3 pl-[66px] text-sm leading-relaxed">
          <span style={{ color: skin.primaryText }}>{bodyText}</span>
        </div>

        {hasMedia ? (
          <div className="px-8">
            <div
              className="overflow-hidden rounded-xl border"
              style={{ borderColor: skin.cardBorder }}
            >
              <MediaSlot
                ratio={TWITTER_AD_MEDIA_RATIO}
                imageUrl={imageUrl}
                videoUrl={videoUrl}
                isLoading={isLoading}
                emptyHint={emptyHint}
                alt={`${brandName} promoted post creative`}
                overlay={
                  hasDuration ? (
                    <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2 bg-gradient-to-t from-black/60 to-transparent px-3 pb-2 pt-6">
                      {/*
                        A ZERO-WIDTH playhead, not the frame's ~4%. Nothing has
                        played; a preview that shows otherwise is inventing a
                        session that never happened.
                      */}
                      <div className="h-[3px] w-full rounded-full bg-white/40">
                        <div className="h-full w-0 rounded-full bg-white" />
                      </div>
                      <div className="flex items-center gap-3 text-white">
                        <PlatformPlayIcon size={14} />
                        <span className="ml-auto text-detail tabular-nums">
                          0:00 / {timecode(videoDurationSeconds!)}
                        </span>
                        <PlatformVolumeMuteIcon size={16} />
                        <PlatformExtendIcon size={14} />
                      </div>
                    </div>
                  ) : undefined
                }
              />
              {/*
                Chrome, not data — every promoted post carries it, so unlike
                the counts below it is unconditional.
              */}
              <div className="px-4 py-3">
                <div
                  className="flex h-9 items-center justify-center rounded-full text-sm font-semibold"
                  style={{ backgroundColor: skin.ctaBackground, color: skin.ctaText }}
                >
                  Follow
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="px-8 pb-1">
            <p className="text-detail" style={{ color: skin.secondaryText }}>
              {emptyHint}
            </p>
          </div>
        )}

        {hasEngagement && (
          <div
            className="flex items-center justify-between px-8 pb-3 pt-3 text-detail"
            style={{ color: skin.secondaryText }}
          >
            <span className="flex items-center gap-2">
              <PlatformTwCommentIcon size={16} />
              {replies != null && <span className="tabular-nums">{compactNumber(replies)}</span>}
            </span>
            <span className="flex items-center gap-2">
              <PlatformTwRetweetIcon size={16} />
              {reposts != null && <span className="tabular-nums">{compactNumber(reposts)}</span>}
            </span>
            <span className="flex items-center gap-2">
              <PlatformTwLikeIcon size={14} />
              {likes != null && <span className="tabular-nums">{compactNumber(likes)}</span>}
            </span>
            <PlatformTwShareIcon size={14} />
          </div>
        )}
      </article>
    </div>
  );
};
